import SidebarCollapse from "@builder/components/app/SidebarCollapse";
import { EditableProps } from "@builder/components/editable/editableProps";
import QueryRadio from "@builder/components/editable/editor/Query/QueryRadio";
import OliverText from "@builder/components/general/OliverText";
import SelectInput from "@builder/components/general/SelectInput";
import { getSelectedNode } from "@builder/store/slices/BuilderSlice/builderSelectors";
import { updateNodeQuery } from "@builder/store/slices/BuilderSlice/builderSlice";
import { useAppDispatch, useAppSelector } from "@builder/store/store";
import { QueryDataTables, QueryFieldsProducts, QueryOrderByOptionsProducts } from "@common/build/constants";
import { BuildNodeQuery } from "@common/build/types";
import { useEffect, useState, useCallback } from "react";
import useDebouncedCallback from "@builder/hooks/useDebounceCallback";
import { useGetCategoriesQuery } from "@builder/store/slices/dataApiSlices/categoriesApiSlice";
import { skipToken } from "@reduxjs/toolkit/query";
import { MultiSelect } from "@builder/components/general/SelectInputNew/MultiSelect";
import { Category } from "@builder/types/Categories.type";
import NumericInput from "@builder/components/general/NumericInput";

const ProductQuery = ({ parent, excluded }: EditableProps) => {
    const dispatch = useAppDispatch();
    const node = useAppSelector(getSelectedNode);
    const { company, data, isOrg } = useAppSelector(state => state.storeData);
    const { company: selectedCompany } = data || {};
    const query = (node?.query || {}) as BuildNodeQuery<"products">;

    const [limit, setLimit] = useState<number>(query.limit || 25);

    // Order By
    const [orderBy, setOrderBy] = useState<QueryOrderByOptionsProducts | undefined>(query.order?.orderBy as QueryOrderByOptionsProducts);
    const orderByOptions = {
        [QueryOrderByOptionsProducts.NAME]: "Name",
        [QueryOrderByOptionsProducts.PRICE]: "Price"
    };

    // Order Direction
    const [order, setOrder] = useState<"ascending" | "descending">(query.order?.orderDirection || "ascending");
    const orderOptions = {
        ascending: "Ascending",
        descending: "Descending"
    };

    // Match Field
    const [field, setField] = useState<QueryFieldsProducts>(query.match?.field as QueryFieldsProducts);
    const fieldOptions = [
        { value: QueryFieldsProducts.ALL, label: "All" },
        { value: QueryFieldsProducts.ONSALE, label: "On Sale" },
        ...(excluded?.includes("category") ? [] : [{ value: QueryFieldsProducts.CATEGORIES, label: "Categories" }])
    ];

    const [debouncedSelectedOptions, setDebouncedSelectedOptions] = useState<Category[]>([]);

    const { data: categoriesData, isLoading: isCategoriesLoading } = useGetCategoriesQuery(
        company?._id && selectedCompany?._id ? { companyId: selectedCompany._id, ...(isOrg && { organizationId: company._id }) } : skipToken
    );

    useEffect(() => {
        if (query.match?.criteria) {
            setDebouncedSelectedOptions([
                ...(query.match?.criteria as string[])?.reduce((acc: Category[], criteria) => {
                    const category = categoriesData?.find(category => category._id === criteria);
                    if (category) {
                        acc.push(category);
                    }
                    return acc;
                }, [])
            ]);
        }
    }, [categoriesData]);

    const handleRadioSelect = (options: Category[]) => {
        if (options) {
            setDebouncedSelectedOptions(options);
        }
    };

    const applyQuery = useCallback(() => {
        if (isCategoriesLoading) return;
        const newQuery: BuildNodeQuery<"products"> = {
            dataType: QueryDataTables.PRODUCTS,
            limit
        };

        if (orderBy) {
            newQuery.order = {
                orderBy,
                orderDirection: order || "ascending"
            };
        }

        if (field && field !== "all") {
            newQuery.match = {
                field,
                criteria: debouncedSelectedOptions.map(option => option._id)
            };
        }

        dispatch(updateNodeQuery(newQuery));
    }, [dispatch, limit, orderBy, order, field, debouncedSelectedOptions]);

    const debouncedApplyQuery = useDebouncedCallback(applyQuery, 300);

    // Auto-apply query when any filter changes
    useEffect(() => {
        debouncedApplyQuery();
    }, [orderBy, order, field, debouncedSelectedOptions, limit]);

    return (
        <>
            <SidebarCollapse title="Query" parent={parent}>
                <NumericInput title="Limit" value={limit} callback={setLimit} />
                <SelectInput
                    title="Order by"
                    options={orderByOptions}
                    value={orderBy}
                    callback={(value: string) => setOrderBy(value as QueryOrderByOptionsProducts)}
                />
                <SelectInput
                    title="Order"
                    options={orderOptions}
                    value={order}
                    callback={(value: string) => setOrder(value as "ascending" | "descending")}
                />
                <div className="dividing-line"></div>
                <OliverText text="Query" size="xs" />
                <OliverText className="query-para-text" text="Results will update automatically as you change filters." size="xxs" />
                <QueryRadio
                    selected={field}
                    options={fieldOptions}
                    isChecked={(option, selected) => option.value === selected}
                    getLabel={option => option.label}
                    callback={option => setField(option.value as QueryFieldsProducts)}
                />
                {query.match?.field === QueryFieldsProducts.CATEGORIES && !excluded?.includes("category") && (
                    <MultiSelect
                        // label={"Select categories to include"}
                        placeholder={"Select categories"}
                        options={categoriesData || []}
                        value={debouncedSelectedOptions}
                        onChange={options => handleRadioSelect(options || ([] as Category[]))}
                        getOptionValue={option => option._id}
                        getOptionLabel={option => option.name || ""}
                        height="small"
                    />
                )}
            </SidebarCollapse>
        </>
    );
};

export default ProductQuery;
